import apiClient from './api';
import { Event, CreateEventDto, UpdateEventDto } from '@/types/event';

export interface EventFilter {
  categories?: string[];
  priceRange?: 'all' | 'free' | 'paid';
  dateRange?: 'all' | 'today' | 'thisWeek' | 'thisMonth';
  availability?: 'all' | 'available' | 'almostFull';
  status?: 'all' | 'active' | 'cancelled' | 'finished';
}

export interface EventResponse {
  id: string;
  title: string;
  description: string;
  eventDate: string;
  time: string;
  location: string;
  locationAddress: string;
  imageUrl: string;
  price: number;
  priceType: string;
  category: string;
  maxAttendees?: number;
  currentAttendees: number;
  placeId?: string;
  placeName?: string;
  placeLogo?: string;
  organizer: string;
  organizerAvatar?: string;
  registrationUrl?: string;
  tags: string[];
  requirements?: string[];
  includes: string[];
  status: string;
  createdAt: string;
  updatedAt: string;
}

// Convert API response to our Event interface
const mapEventResponse = (response: EventResponse): Event => ({
  id: response.id,
  title: response.title,
  description: response.description,
  eventDate: new Date(response.eventDate),
  startTime: response.time.split(' - ')[0],
  endTime: response.time.split(' - ')[1],
  location: response.location,
  locationAddress: response.locationAddress,
  imageUrl: response.imageUrl,
  price: response.price,
  priceType: response.priceType.toLowerCase() as 'free' | 'paid',
  category: response.category.toLowerCase().replace(' ', '_') as Event['category'],
  maxAttendees: response.maxAttendees,
  currentAttendees: response.currentAttendees,
  placeId: response.placeId,
  placeName: response.placeName,
  placeLogo: response.placeLogo,
  organizer: response.organizer,
  organizerAvatar: response.organizerAvatar,
  registrationUrl: response.registrationUrl,
  tags: response.tags,
  requirements: response.requirements,
  includes: response.includes,
  status: response.status.toLowerCase() as 'active' | 'cancelled' | 'finished',
  createdAt: new Date(response.createdAt),
  updatedAt: new Date(response.updatedAt)
});

export const eventService = {
  // Get all events (admin) - backend handles filtering based on user permissions
  async getAllEvents(): Promise<Event[]> {
    try {
      const response = await apiClient.get('/admin/adminEvents');
      const data: EventResponse[] = response.data;
      return data.map(mapEventResponse);
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  },

  // Get event by ID
  async getEventById(id: string): Promise<Event | null> {
    try {
      const response = await apiClient.get(`/admin/adminEvents/${id}`);
      const data: EventResponse = response.data;
      return mapEventResponse(data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Error fetching event:', error);
      throw error;
    }
  },

  // Create event
  async createEvent(eventData: CreateEventDto): Promise<Event> {
    try {
      // Convert placeId and cityId to null if they're empty strings or undefined
      const processedData = {
        ...eventData,
        placeId: eventData.placeId && eventData.placeId.trim() !== '' ? eventData.placeId : null,
        cityId: eventData.cityId && eventData.cityId.trim() !== '' ? eventData.cityId : null
      };
      
      const response = await apiClient.post('/admin/adminEvents', processedData);
      const data: EventResponse = response.data;
      return mapEventResponse(data);
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  },

  // Update event
  async updateEvent(id: string, eventData: UpdateEventDto): Promise<Event> {
    try {
      // Convert placeId and cityId to null if they're empty strings or undefined
      const processedData = {
        ...eventData,
        id,
        placeId: eventData.placeId && eventData.placeId.trim() !== '' ? eventData.placeId : null,
        cityId: eventData.cityId && eventData.cityId.trim() !== '' ? eventData.cityId : null
      };
      
      const response = await apiClient.put(`/admin/adminEvents/${id}`, processedData);
      const data: EventResponse = response.data;
      return mapEventResponse(data);
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  },

  // Delete event
  async deleteEvent(id: string): Promise<boolean> {
    try {
      await apiClient.delete(`/admin/adminEvents/${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  },

  // Check if user can manage event
  async canManageEvent(id: string): Promise<boolean> {
    try {
      const response = await apiClient.get(`/admin/adminEvents/${id}/can-manage`);
      return response.data;
    } catch (error) {
      console.error('Error checking event management permissions:', error);
      return false;
    }
  },

  // Filter events
  async filterEvents(filters: EventFilter): Promise<Event[]> {
    try {
      let events = await this.getAllEvents();
      
      // Apply category filter
      if (filters.categories && filters.categories.length > 0) {
        events = events.filter(event => 
          filters.categories!.includes(event.category)
        );
      }
      
      // Apply price range filter
      if (filters.priceRange && filters.priceRange !== 'all') {
        events = events.filter(event => 
          filters.priceRange === 'free' ? event.priceType === 'free' : event.priceType === 'paid'
        );
      }
      
      // Apply date range filter
      if (filters.dateRange && filters.dateRange !== 'all') {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        events = events.filter(event => {
          const eventDate = new Date(event.eventDate);
          eventDate.setHours(0, 0, 0, 0);
          
          switch (filters.dateRange) {
            case 'today':
              return eventDate.getTime() === today.getTime();
            case 'thisWeek':
              const weekFromNow = new Date(today);
              weekFromNow.setDate(today.getDate() + 7);
              return eventDate >= today && eventDate <= weekFromNow;
            case 'thisMonth':
              const monthFromNow = new Date(today);
              monthFromNow.setMonth(today.getMonth() + 1);
              return eventDate >= today && eventDate <= monthFromNow;
            default:
              return true;
          }
        });
      }
      
      // Apply availability filter
      if (filters.availability && filters.availability !== 'all') {
        events = events.filter(event => {
          if (!event.maxAttendees) return true;
          
          const available = event.maxAttendees - event.currentAttendees > 0;
          const almostFull = event.maxAttendees - event.currentAttendees <= 5;
          
          switch (filters.availability) {
            case 'available':
              return available;
            case 'almostFull':
              return almostFull && available;
            default:
              return true;
          }
        });
      }
      
      // Apply status filter
      if (filters.status && filters.status !== 'all') {
        events = events.filter(event => event.status === filters.status);
      }
      
      return events;
    } catch (error) {
      console.error('Error filtering events:', error);
      return [];
    }
  }
};